import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { CMSNoResultsFound } from '@/features/cms/components/CMS/components/Shelves/ProductShelf/CMSNoResultsFound'
import { Circle360ActivationHero } from '@/features/entrypoint/components/TargetCircle360Page/Circle360ActivationHero'
import { Circle360NMUBanner } from '@/features/shop/components/RetailerHomepage/Circle360NMUBanner'
import { MembershipEntryPointBanner } from '@/features/account/components/MembershipEntryPointBanner'
import { Circle360EvergreenNMUBanner } from '@/features/entrypoint/components/Circle360/Circle360EvergreenNMUBanner'
import { ServiceFeeDisclaimer } from '@/features/shop/components/RetailerHomepage/ServiceFeeDisclaimer'
import { Circle360ValueProposition } from '@/features/entrypoint/components/TargetCircle360Page/Circle360ValueProposition'
import { UngatedHomepageValueProposition } from '@/features/entrypoint/components/UngatedHomePage/ValueProposition'
import {
  C360SavingsPoints,
  UngatedHomepageSavingsPoints,
} from '@/features/entrypoint/components/Circle360/SavingsPoints'
import { AnimatedHero } from '@/features/entrypoint/components/TargetCircle360Page/AnimatedHero'
import { AnimatedHomepageHero } from '@/features/entrypoint/components/UngatedHomePage/HomepageRefresh/AnimatedHomepageHero'
import { ValuePropCard } from '@/features/entrypoint/components/UngatedHomePage/ValuePropCard'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { HomepageHeroBanner } from '@/features/entrypoint/components/UngatedHomePage/HomepageHeroBanner'
import { KitsShelf } from '@/features/shop/components/Kits/KitsShelf'
import { UngatedWebForm } from '@/features/entrypoint/components/Marketing/UngatedWebForm'
import { PersonaCompareProfiles } from '@/features/business/components/PersonaCompareProfiles'

const renderComponent = (props: PlaceholderData & { index?: number }) => {
  const {
    data: { component_name },
    content_type_id,
    id,
    index,
  } = props
  switch (component_name) {
    case 'membership_entry_point_banner':
      return <MembershipEntryPointBanner id={id} content_type_id={content_type_id} />
    case 'circle_360_activation_hero':
      return <Circle360ActivationHero id={id} content_type_id={content_type_id} />
    case 'circle_360_value_proposition':
      return <Circle360ValueProposition id={id} content_type_id={content_type_id} />
    case 'homepage_hero_banner':
      return <HomepageHeroBanner {...props} />
    case 'homepage_value_prop_1':
      return (
        <ValuePropCard
          {...props}
          title="Shop from 120+ stores"
          subtitle="From groceries to last-minute gifts, shop local favorites and national retailers."
          imgMobile={objectStorageUrl('stores-vp-mweb.webp')}
          imgDesktop={objectStorageUrl('stores-vp-dweb.webp')}
          trackingName="value_prop_120_stores"
        />
      )
    case 'homepage_value_prop_2':
      return (
        <ValuePropCard
          {...props}
          title="Get excellent service and same-day delivery"
          subtitle="Your orders are in dependable, detail-oriented hands."
          imgMobile={objectStorageUrl('sameday-service-mweb.webp')}
          imgDesktop={objectStorageUrl('sameday-service-dweb.webp')}
          trackingName="value_prop_excellent_service"
        />
      )
    case 'ungated_hp_value_proposition':
      return <UngatedHomepageValueProposition id={id} content_type_id={content_type_id} />
    case 'c360_nmu':
      return <Circle360NMUBanner id={id} content_type_id={content_type_id} />
    case 'c360_evergreen_nmu':
      return <Circle360EvergreenNMUBanner id={id} content_type_id={content_type_id} />
    case 'service_fee_disclaimer':
      return <ServiceFeeDisclaimer id={id} content_type_id={content_type_id} />
    case 'c360_savings_points':
      return <C360SavingsPoints id={id} content_type_id={content_type_id} />
    case 'ungated_hp_savings_points':
      return <UngatedHomepageSavingsPoints id={id} content_type_id={content_type_id} />
    case 'c360_animated_hero':
      return <AnimatedHero id={id} content_type_id={content_type_id} />
    case 'homepage_animated_hero':
      return <AnimatedHomepageHero id={id} content_type_id={content_type_id} />
    case 'shipt_kits_shelf':
      return <KitsShelf id={id} content_type_id={content_type_id} index={index} />
    case 'no_results_found':
      return <CMSNoResultsFound content_type_id={content_type_id} id={id} />
    case 'ungated_web_form':
      return <UngatedWebForm id={id} content_type_id={content_type_id} />
    case 'business_persona_compare_profiles':
      return <PersonaCompareProfiles id={id} content_type_id={content_type_id} />
    default:
      return null
  }
}

/**
 * This component corresponds to the `segway_placeholder` and `fe_placeholder` content type in the CMS. `segway_placeholder` is
 * still in code to preserve backwards compatibility
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/fe_placeholder
 * - **Production** - https://cms.shipt.com/content-type/pool/fe_placeholder
 */
export const Placeholder = (props: PlaceholderData & { index?: number }) => {
  try {
    return renderComponent(props)
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={props.content_type_id} id={props.id} />
  }
}
